/**
 * A global class that supports many commonly used functions throughout an application.
 */

import { DataRequest } from "./Data";

export interface ImageSize {
    Width: number;
    Height: number;
}

export default class Utility {

    // Encoding/decoding

    /**
     * Encodes a string that contains HTML tags.
     * @param html string to be encoded.
     * @returns An encoded string where HTML tags are replaced with displayable characters.
     */
    public static encodeHTML(html: string): string {
        let textArea = Utility.cachedTextArea;
        if (!textArea)
            textArea = Utility.cachedTextArea = document.createElement("textarea");
        textArea.innerHTML = html;
        const text = textArea.innerHTML;
        return text;
    }
    private static cachedTextArea: HTMLTextAreaElement|null = null;

    // Date/Time formatting

    // Default IANA timezone used to format all date/time values; empty = browser local.
    // Set once at app startup (App.vue) from import.meta.env.VITE_TimeZone.
    private static timeZone: string = "";
    // Default date format (MM = month, dd = day, yyyy = year); set from import.meta.env.VITE_DateFormat.
    private static dateFormat: string = "MM/dd/yyyy";
    private static dateFormatter: Intl.DateTimeFormat | null = null;
    private static timeFormatter: Intl.DateTimeFormat | null = null;

    public static setDefaultTimeZone(timeZone: string | null | undefined): void {
        this.timeZone = timeZone || "";
        this.dateFormatter = null;
        this.timeFormatter = null;
    }

    public static setDefaultDateFormat(dateFormat: string | null | undefined): void {
        this.dateFormat = dateFormat || "MM/dd/yyyy";
    }

    public static getFormattedDateTimeHHMMSS(date: Date | null): string {
        if (!date || isNaN(date.getTime())) return "";
        return this.getFormattedDate(date) + " " + this.formatTime(date, true);
    }
    public static getFormattedDateTime(date: Date | null): string {
        if (!date || isNaN(date.getTime())) return "";
        return this.getFormattedDate(date) + " " + this.formatTime(date, false);
    }
    public static getFormattedDate(date: Date | null): string {
        if (!date || isNaN(date.getTime())) return "";
        this.dateFormatter ??= new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: this.timeZone || undefined });
        const formatted = this.dateFormatter.format(date); // en-US MM/DD/YYYY in the configured zone
        const month = formatted.substring(0, 2);
        const day = formatted.substring(3, 5);
        const year = formatted.substring(6);
        return this.applyDateFormat(month, day, year);
    }
    private static applyDateFormat(month: string, day: string, year: string): string {
        return this.dateFormat.replace("MM", month).replace("dd", day).replace("yyyy", year);
    }
    private static formatTime(date: Date, withSeconds: boolean): string {
        this.timeFormatter ??= new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: this.timeZone || undefined });
        const formatted = this.timeFormatter.format(date);
        let h = Number(formatted.substring(0, 2));
        const m = formatted.substring(3, 5);
        const s = formatted.substring(6, 8);
        if (h === 24) h = 0; // Intl can emit "24" for midnight with hour12:false
        let hour = h % 12;
        if (hour === 0) hour = 12;
        const ampm = h < 12 ? "AM" : "PM";
        return withSeconds ? `${this.zeroLeftPad(hour, 2)}:${m}:${s} ${ampm}` : `${this.zeroLeftPad(hour, 2)}:${m} ${ampm}`;
    }
    public static zeroLeftPad(val: number, pos: number): string {
        if (val < 0) return val.toFixed();
        let s = val.toFixed(0);
        while (s.length < pos)
            s = "0" + s;
        return s;
    }

    // DateOnly

    /**
     * Format a C# DateOnly. Unfortunately we're not using .net6 everywhere so this arrives as a DateTimeOffset with timezone +00:00
     * The parts are read straight out of the string rather than through the configured timezone: a
     * DateOnly is a wall-clock date, and converting midnight UTC into a zone behind UTC would move it
     * to the previous day.
     * @param date string (UTC+0) to be formatted as a displayable date.
     * @returns A formatted date string.
     */
    public static getFormattedDateOnly(date: string | null): string {
        if (!date) return "";
        if (!date.endsWith("T00:00:00+00:00")) return "(invalid)";
        const d = date.substring(8,8+2);
        const m = date.substring(5,5+2);
        const y = date.substring(0,0+4);
        return this.applyDateFormat(m, d, y);
    }

    // Currency

    public static formatCurrencyDisplay(value: number): string {
        const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2
        });
        return formatter.format(value);
    }

    public static parseCurrency(n: number): number {
        const multiplicator = Math.pow(10, 2);
        n = parseFloat((n * multiplicator).toFixed(11));
        const result = (Math.round(n) / multiplicator);
        return +(result.toFixed(2));
    }

    // KB, MB, GB

    public static formatSizeInKB(size: number): string {
        if (size < 1024) return `${size.toString()} bytes`;
        if (size < 1024*1024) return `${(size / 1024).toFixed(1)} KB`;
        if (size < 1024*1024*1024) return `${(size / (1024*1024)).toFixed(1)} MB`;
        return `${(size / (1024*1024*1024)).toFixed(1)} GB`;
    }

    // URL

    /**
     *
     * @param url Format a complete URL. E.g., formatUrl("/sample/page", { id: 123, search: "abc:"});
     * @param query Query string arguments.
     * @param domain Optional domain (https://......./). If not supplied, the current domain is used.
     * @param auth Optionally adds bearer token as query string argument.
     * @returns A formatted URL.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public static formatUrl(url: string, query: any, domain?: string, auth?: boolean): string {
        if (!domain)
            domain = `${window.origin}/`;
        if (url.startsWith("/")) url = url.substring(1);
        let newUrl = `${domain}${url}`;
        let first = true;
        if (query) {
            for (const propertyName in query) {
                if (first)
                    newUrl += "?";
                else
                    newUrl += "&";
                first = false;
                newUrl += `${encodeURIComponent(propertyName)}=`;
                const val = query[propertyName];
                if (val !== undefined && val != null)
                    newUrl += `${encodeURIComponent(val)}`;
            }
        }
        if (auth) {
            // add bearer token
            if (DataRequest.getToken && DataRequest.haveToken) {
                const token = DataRequest.getToken();
                if (first)
                    newUrl += "?";
                else
                    newUrl += "&";
                first = false;
                newUrl += "__BearerToken=";
                if (token !== undefined && token != null)
                    newUrl += token;
            }
        }
        return newUrl;
    }

    /**
     *
     * @param url Format a complete path (without schema and domain). E.g., formatUrl("/sample/page", { id: 123, search: "abc:"});
     * @param query Query string arguments.
     * @returns A formatted URL.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public static formatPath(url: string, query: any): string {
        let newUrl = url;
        let first = true;
        if (query) {
            for (const propertyName in query) {
                if (first)
                    newUrl += "?";
                else
                    newUrl += "&";
                first = false;
                newUrl += `${encodeURIComponent(propertyName)}=`;
                const val = query[propertyName];
                if (val !== undefined && val != null)
                    newUrl += `${encodeURIComponent(val)}`;
            }
        }
        return newUrl;
    }

    // Image Size

    public static getImageDimensions(file: File): Promise<ImageSize> {

        let resolvePromise: (value: ImageSize) => void;
        let rejectPromise: (reason?: any) => void;
        const promise = new Promise<ImageSize>((resolve, reject):void => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });

        const img = document.createElement("img") as HTMLImageElement;
        const blob = URL.createObjectURL(file);
        img.onload = (): void => {
            const w = img.width;
            const h = img.height;
            img.remove();
            resolvePromise({ Width: w, Height: h });
        };
        img.onerror = (): void => {
            img.remove();
            rejectPromise();
        };
        img.src = blob;

        return promise;
    }

    public static formatSerialNumber(serial: string): string {
        const arr = serial.match(/.{1,4}/g);
        if (arr)
            serial = arr.join("-");
        return serial;
    }

    // Page Reload/Change

    // Page modification support (used with onbeforeunload)
    public static get pageChanged(): boolean {
        return this.pageChangedCache;
    }
    public static set pageChanged(value: boolean) {
        if (this.pageChangedCache !== value) {
            this.pageChangedCache = value;
        }
    }
    private static pageChangedCache: boolean = false;
}
